import type { MouseEvent } from 'react'

/**
 * Opening a card by clicking ANYWHERE on it that is not a control.
 *
 * Ruled by Erik on 2026-09-23: *"in the design you can click anywhere on the
 * entire card as long as it isn't a button to open the card. Currently you have
 * to click the title."* Three of this app's cards put the open on the title
 * alone, so the eighty per cent of the card that reads as clickable was not.
 *
 * THE TITLE STAYS A REAL BUTTON, and that is the point of doing it this way
 * rather than by making the card one. A card is a `li` or an `article` with
 * verbs inside it, and a button cannot contain a button: turning the card into
 * one would either swallow Complete and Approve or produce invalid markup that
 * behaves differently in every browser. So the container takes a click handler,
 * the title keeps its button, and the keyboard path is unchanged: tab reaches
 * the title, Enter opens it, and nothing new is in the tab order.
 *
 * WHAT COUNTS AS A CONTROL is asked of the DOM rather than listed by class,
 * because the list would go stale the first time somebody adds a select to a
 * card. `closest` walks up from whatever was actually clicked, so a click on
 * the text inside a button counts as the button.
 *
 * SELECTING TEXT IS NOT CLICKING. A person who drags across a title to copy it
 * has not asked for anything to open, and without this the card opens under
 * them and takes the selection with it.
 */
const CONTROLS = 'button, a, select, input, textarea, label, [role="button"], [contenteditable]'

export function openOnCardClick(onOpen?: () => void) {
  if (!onOpen) return undefined
  return (event: MouseEvent) => {
    const target = event.target as HTMLElement | null
    if (target?.closest(CONTROLS)) return
    if ((window.getSelection()?.toString() ?? '') !== '') return
    onOpen()
  }
}
